import type { TWorkerResponse } from './normalizeRuleset.worker';
import { IRoutingRuleset } from './types';

let _worker: null | Worker = null;
let _workerFailed = false;

const getWorker = (): null | Worker => {
  if (typeof window === 'undefined' || _workerFailed) return null;
  if (_worker) return _worker;

  try {
    _worker = new Worker(new URL('./normalizeRuleset.worker.ts', import.meta.url), {
      name: 'ruleset-normalizer',
      type: 'module',
    });
  } catch {
    _workerFailed = true;

    return null;
  }

  return _worker;
};

export const normalizeRulesetInWorker = <T = IRoutingRuleset>(
  data: unknown,
  searchString?: string,
): Promise<T> => {
  const worker = getWorker();

  if (!worker) {
    return Promise.reject(new Error('Worker not available'));
  }

  return new Promise((resolve, reject) => {
    const controller = new AbortController();
    const { signal } = controller;

    worker.addEventListener(
      'message',
      (e: MessageEvent<TWorkerResponse>) => {
        controller.abort();

        if (e.data.kind === 'done') {
          resolve(e.data.result as T);
        } else {
          reject(new Error(e.data.message));
        }
      },
      { signal },
    );

    worker.addEventListener(
      'error',
      (e: ErrorEvent) => {
        controller.abort();
        _worker = null;
        _workerFailed = true;
        reject(new Error(e.message || 'Worker error'));
      },
      { signal },
    );

    worker.postMessage({
      kind: 'normalizePageData',
      payload: { data, searchString },
    });
  });
};
